import { apiGatewayClient, drProfileClient, hamdastClient } from '@/common/apis/client';
import { getDoctorBiography } from '@/common/apis/services/doctor/getDoctorBiography';
import { getDoctorMedicalCode } from '@/common/apis/services/doctor/getDoctorMedicalCode';
import { getDoctorCenters } from '@/common/apis/services/doctor/getDoctorCenters';
import { getDoctorCenterServices } from '@/common/apis/services/doctor/getDoctorCenterServices';
import { getDoctorExpertise } from '@/common/apis/services/doctor/getDoctorExpertise';
import { getDoctorFullName } from '@/common/apis/services/doctor/getDoctorFullName';
import { getDoctorImage } from '@/common/apis/services/doctor/getDoctorImage';
import { getDoctorOnlineChannels } from '@/common/apis/services/doctor/getDoctorOnlineChannels';
import { getDoctorKeywordTags } from '@/common/apis/services/doctor/getDoctorKeywordTags';
import { getDoctorInsuranceFromBiko } from '@/common/apis/services/biko/getDoctorInsurance';
import { internalLinks } from '@/common/apis/services/profile/internalLinks';
import { getReviews } from '@/common/apis/services/reviews/getReviews';
import { CENTERS } from '@/common/types/centers';
import { fetchDoctorGalleryFromDrprofile } from '@/modules/profile/utils/fetchDoctorGalleryFromDrprofile';
import { fetchDoctorCenterFacilitiesMap } from '@/modules/profile/functions/fetchDoctorCenterFacilities';
import { buildRismanDoctorId } from '@/modules/profile/utils/buildRismanDoctorId';
import { dropStaleRismanSlotTime } from '@/modules/profile/utils/dropStaleRismanSlotTime';
import { withoutHamdastBimehnamaWidgets } from '@/modules/profile/utils/bikoInsuranceProfile';
import { withoutHamdastCheraghWidgets } from '@/modules/profile/utils/cheraghFacilitiesProfile';
import { computeExperienceYearsFromGraduationDate } from '@/modules/profile/utils/computeExperienceYears';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import { getAverageWaitingTime } from './getAverageWaitingTime';
import { getRateDetailsData } from './getRateDetailsData';
import { OverwriteProfileData, overwriteProfileData } from './overwriteProfileData';

const RISMAN_DOCTORS = 'https://apigw.paziresh24.com/v1/risman/doctors/';
const HAMDAST_WIDGETS = '/api/v1/widgets/';
const IGNORED_CENTER_ID = '5532';

/** A piece that has not arrived yet. `null` means it arrived but failed / was skipped. */
export type Pending = undefined;

export type ProfilePieces = {
  slugInfo?: any | null;
  validatedSlug?: string;
  fullProfile?: Record<string, any> | null;
  rateDetails?: any | null;
  reviews?: any | null;
  waitingTime?: any[] | null;
  risman?: any | null;
  fullName?: { name?: string | null; family?: string | null } | null;
  expertise?: any[] | null;
  image?: { image?: string } | null;
  biography?: { biography?: string } | null;
  medicalCode?: string;
  centers?: any[] | null;
  keywordTags?: any[];
  gallery?: any[] | null;
  pageView?: number | null;
  onlineServices?: any[] | null;
  onlineChannels?: string[];
  internalLinks?: any[] | null;
  userData?: any | null;
  widgets?: { widgets: any[]; widgetsData: any };
  bikoInsurance?: any | null;
  centerFacilities?: Record<string, any>;
  samanAvailability?: any | null;
};

const settled = <T,>(p: Promise<T>, fallback: T) => Promise.resolve(p).catch(() => fallback);

export const fetchRateDetails = (slug: string) => settled<any>(getRateDetailsData({ slug }), null);
export const fetchReviews = (slug: string) => getReviews({ slug, sort: 'default_order' });
export const fetchWaitingTime = (slug: string) => getAverageWaitingTime({ slug });

/** Rejects on network failure (caller maps rejection → null); no id → dropStale(null) as before. */
export const fetchRisman = async (slugInfo: any) => {
  const id = buildRismanDoctorId(slugInfo?.owner_id, slugInfo?.server_id);
  const { data } = id
    ? await axios.get(RISMAN_DOCTORS, { params: { doctor_id: id }, timeout: 1500 })
    : { data: null };
  return dropStaleRismanSlotTime(data);
};

export const fetchFullName = (slug: string) => getDoctorFullName(slug);
export const fetchExpertise = (slug: string) => getDoctorExpertise(slug);
export const fetchImage = (slug: string) => getDoctorImage(slug);

/** Parallel fan-out can drop biography on prod; one dedicated retry when empty (retry is not caught, as before). */
export const fetchBiography = async (slug: string) => {
  const first: any = await settled<any>(getDoctorBiography(slug), null);
  if (typeof first?.biography === 'string' && first.biography.trim().length > 0) return first;
  return getDoctorBiography(slug);
};

export const fetchMedicalCode = async (slug: string, slugInfo: any): Promise<string> => {
  const r: any = await settled<any>(
    getDoctorMedicalCode(slug, { id: slugInfo?.id ?? slugInfo?.owner_id, server_id: slugInfo?.server_id }),
    null,
  );
  return r?.medical_code?.trim() || '';
};

export const fetchCenters = (slug: string, university?: string) => getDoctorCenters(slug, university, { onlyBookable: false });

export const fetchKeywordTags = (slug: string, fullName: any, centers: any[] | null) => {
  const processedName = fullName?.name;
  const processedFamily = fullName?.family;
  const finalName = processedName === null ? '' : processedName ?? '';
  const finalFamily = processedFamily === null ? '' : processedFamily ?? '';
  const searchText = [finalName, finalFamily].filter(Boolean).join(' ').trim();
  const city =
    (centers as any)?.find?.((center: any) => center?.id !== IGNORED_CENTER_ID && center?.city?.slug)?.city?.slug ||
    (centers as any[])?.find?.((center: any) => center?.id !== IGNORED_CENTER_ID && center?.city_en_slug)?.city_en_slug ||
    'ir';
  return getDoctorKeywordTags({
    slug,
    searchText: searchText || undefined,
    cityEnSlug: typeof city === 'string' ? city : 'ir',
  }).catch(() => []);
};

export const fetchGallery = async (
  slug: string,
  university?: string,
  preloadedCenters?: any[] | null,
): Promise<any[] | null> => {
  try {
    return await fetchDoctorGalleryFromDrprofile(slug, university, preloadedCenters ?? undefined);
  } catch (error) {
    console.error('Error fetching doctor gallery:', error);
    return null;
  }
};

export const fetchPageView = async (slug: string): Promise<number | null> => {
  try {
    const { data: pageViewData } = await drProfileClient.get(`/api/doctors/${encodeURIComponent(slug)}/page-view`, {
      timeout: 2000,
    });
    const d: any = pageViewData;
    const candidates = [
      d?.page_view,
      d?.count_of_page_view,
      d?.number_of_visits,
      d?.views,
      d?.view,
      d?.data?.page_view,
      d?.data?.count_of_page_view,
      d?.data?.number_of_visits,
    ];
    const n = candidates.find(value => typeof value === 'number');
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
  } catch (error) {
    console.error('Error fetching doctor page-view from drProfile service:', error);
    return null;
  }
};

export const fetchOnlineServices = async (slug: string): Promise<{ services: any[] | null; channels: string[] }> => {
  const [s, c] = await Promise.allSettled([getDoctorCenterServices(slug, CENTERS.CONSULT), getDoctorOnlineChannels(slug)]);
  if (s.status === 'rejected') console.error('Error hydrating online visit center services:', s.reason);
  return {
    services: s.status === 'fulfilled' ? (s.value as any) ?? null : null,
    channels: c.status === 'fulfilled' ? (c.value as any) : [],
  };
};

export const fetchInternalLinks = (links: string[]): Promise<any[] | null> =>
  links.length ? settled<any>(internalLinks({ links }), null) : Promise.resolve([]);

export const fetchUserData = async (slug: string) => {
  try {
    const { data } = await drProfileClient.get(`/api/doctors/${encodeURIComponent(slug)}`, { timeout: 3000 });
    return data;
  } catch (error) {
    console.error('Could not resolve doctor user_id from DrProfile. Continuing without it.', error);
    return null;
  }
};

export const fetchWidgets = async (information: any, userData: any): Promise<{ widgets: any[]; widgetsData: any }> => {
  try {
    if (!userData?.user_id) return { widgets: [], widgetsData: {} };

    const { data: widgetsRaw } = await hamdastClient.get(HAMDAST_WIDGETS, {
      params: { user_id: userData.user_id },
      timeout: 3000,
    });

    const widgets = withoutHamdastCheraghWidgets(withoutHamdastBimehnamaWidgets(widgetsRaw));
    if (!widgets || widgets.length === 0) return { widgets: [], widgetsData: {} };

    const dataEndpoints = widgets.filter((item: any) => item.data_endpoint);
    const responses = await Promise.allSettled(
      dataEndpoints.map((item: any) =>
        apiGatewayClient.get(item.data_endpoint, {
          params: { user_id: userData.user_id, doctor_id: information?.id, widget_id: item?.id },
          timeout: 1500,
        }),
      ),
    );

    const widgetsData = responses.reduce((acc: any, current) => {
      if (current.status === 'fulfilled') {
        const widgetId = (current.value as any)?.config?.params?.widget_id;
        if (widgetId) {
          acc[widgetId] = (current.value as any).data;
        }
      }
      return acc;
    }, {});

    const activeWidgets = widgets.filter((item: any) => (item.data_endpoint ? !isEmpty(widgetsData[item.id]) : true));
    return { widgets: activeWidgets, widgetsData };
  } catch (error) {
    console.error('Error fetching widgets data:', error);
    return { widgets: [], widgetsData: {} };
  }
};

/** Soft-cap below-the-fold Biko waits — same budgets as microservice path (avoid legacy TTFB blow-up). */
const withBudget = async <T,>(promise: Promise<T>, budgetMs: number, fallback: T): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>(resolve => {
        timer = setTimeout(() => resolve(fallback), budgetMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export const fetchBikoInsurance = (userId: any) =>
  withBudget<any>(getDoctorInsuranceFromBiko(userId, { timeoutMs: 1500 }), 900, null);

// No outer withBudget: map already times out per center and returns partial successes.
export const fetchCenterFacilities = (userId: any, centers: any[]) =>
  fetchDoctorCenterFacilitiesMap(userId, centers, { timeoutMs: 1200 });

/** Centers source used for the overwrite: centers API when non-empty, else full-profile centers. */
export const centersForOverwrite = (p: ProfilePieces): any[] | null =>
  (p.centers?.length ?? 0) > 0
    ? (p.centers as any[])
    : Array.isArray(p.fullProfile?.centers) && p.fullProfile!.centers.length > 0
      ? p.fullProfile!.centers
      : null;

export const findConsultCenter = (p: ProfilePieces) =>
  centersForOverwrite(p)?.find?.((c: any) => String(c?.id) === CENTERS.CONSULT);

/** Legacy "fail = provider" shape: userData falls back to the provider object built from slugInfo. */
export const providerFromPieces = (p: ProfilePieces) => {
  const experienceYears = computeExperienceYearsFromGraduationDate(p.expertise?.[0]?.graduation_date);
  return {
    user_id: p.slugInfo?.user_id != null ? String(p.slugInfo.user_id) : undefined,
    ...(experienceYears && { experience: experienceYears }),
  };
};

export const resolveEffectiveUserData = (p: ProfilePieces) => p.userData ?? providerFromPieces(p);

export const resolveProfileUserId = (p: ProfilePieces) =>
  resolveEffectiveUserData(p)?.user_id ?? p.slugInfo?.user_id ?? p.fullProfile?.user_id;

// ---------- shared overwrite (pure) — used by assembleProfile and SEO-link resolution ----------

export const buildOverwriteData = (p: ProfilePieces): OverwriteProfileData => {
  const doctorExpertise = p.expertise ?? null;
  const transformedExpertise =
    doctorExpertise?.map((exp: any) => ({
      alias_title: exp.alias_title,
      degree: exp.degree,
      expertise: exp.expertise,
      groups: exp.groups,
      graduation_date: exp.graduation_date ?? null,
    })) ?? [];
  const doctorImage = p.image ?? null;
  const doctorBiography = p.biography ?? null;
  const doctorGallery = p.gallery ?? null;
  const centers = centersForOverwrite(p);
  const rateDetails = p.rateDetails ?? null;
  return {
    provider: providerFromPieces(p),
    feedbacks: {
      ...rateDetails,
      hideRates: rateDetails?.hide_rates,
      reviews: p.reviews === undefined ? {} : p.reviews,
      waiting_time_info: p.waitingTime === undefined ? [] : p.waitingTime,
      countOfFeedbacks: rateDetails?.countOfFeedbacks ?? undefined,
    },
    history: {},
    ...((doctorExpertise?.length ?? 0) > 0 && { expertises: transformedExpertise }),
    ...(doctorImage?.image && { image: doctorImage.image }),
    ...(typeof doctorBiography?.biography === 'string' &&
      doctorBiography.biography.trim().length > 0 && { biography: doctorBiography.biography }),
    ...(p.medicalCode && { councilMedicalCode: p.medicalCode }),
    ...(centers && { centers }),
    ...(doctorGallery && doctorGallery.length > 0 && { gallery: doctorGallery }),
  } as OverwriteProfileData;
};

export const resolveNameParts = (p: ProfilePieces) => {
  const processedName = p.fullName?.name;
  const processedFamily = p.fullName?.family;
  const finalName = processedName === null ? '' : processedName ?? '';
  const finalFamily = processedFamily === null ? '' : processedFamily ?? '';
  return { finalName, finalFamily };
};

export const runOverwrite = (p: ProfilePieces, overwriteData: OverwriteProfileData = buildOverwriteData(p)) => {
  const { finalName, finalFamily } = resolveNameParts(p);
  const profileSource = p.fullProfile && typeof p.fullProfile === 'object' ? p.fullProfile : {};
  const resolvedName = finalName?.trim() ? finalName : undefined;
  const resolvedFamily = finalFamily?.trim() ? finalFamily : undefined;
  return overwriteProfileData(overwriteData, {
    ...profileSource,
    ...(p.slugInfo?.owner_id != null && { id: p.slugInfo.owner_id }),
    ...(p.slugInfo?.server_id != null && { server_id: p.slugInfo.server_id }),
    ...(resolvedName && { name: resolvedName }),
    ...(resolvedFamily && { family: resolvedFamily }),
  });
};

export const resolveExpertisesWithGroups = (p: ProfilePieces, expertises: any) => {
  const normalizedGroupExpertisesSource =
    p.expertise?.flatMap((item: any) => item?.groups ?? []) ?? expertises?.group_expertises ?? [];
  const normalizedGroupExpertises = normalizedGroupExpertisesSource.filter((group: any, index: number, array: any[]) => {
    const slug = group?.en_slug || group?.slug;
    if (!slug) return false;
    return array.findIndex(item => (item?.en_slug || item?.slug) === slug) === index;
  });

  const expertisesWithGroups = {
    ...expertises,
    group_expertises:
      expertises?.group_expertises?.length > 0
        ? expertises.group_expertises
        : normalizedGroupExpertises.map((group: any) => ({
            id: group.id,
            en_slug: group.en_slug ?? group.slug,
            icon: group.icon ?? null,
            name: group.name,
          })),
  };
  return { expertisesWithGroups, normalizedGroupExpertises };
};

const getProdLikeRelatedSpecialityLinks = ({ centers, group_expertises }: any): string[] => {
  const groups = (Array.isArray(group_expertises) ? group_expertises : []).filter((group: any) => {
    const groupId = group?.group_id ?? group?.id;
    return groupId !== 35;
  });
  const uniqueCitySlugs = (Array.isArray(centers) ? centers : [])
    .filter((center: any) => center?.city_en_slug && String(center?.id) !== IGNORED_CENTER_ID)
    .map((center: any) => center.city_en_slug)
    .filter((slug: string, index: number, array: string[]) => array.indexOf(slug) === index);

  const expertiseLinks: string[] = [];
  const consultLinks: string[] = [];
  const cityLinks: string[] = [];

  groups.forEach((group: any) => {
    const slug = group?.en_slug || group?.slug;
    if (!slug) return;
    expertiseLinks.push(`/s/ir/${slug}/`);
    consultLinks.push(`/consult/${slug}/`);
  });
  uniqueCitySlugs.forEach(citySlug => {
    cityLinks.push(`/s/${citySlug}/doctor/`, `/s/${citySlug}/center/`);
  });

  return Array.from(new Set([...expertiseLinks, ...cityLinks, ...consultLinks]));
};

/**
 * Same inputs as before: overwritten centers (online-service hydration only touches the consult
 * center's services, never city slugs) + group expertises normalized from the expertise piece.
 */
export const resolveRelatedSpecialityLinks = (p: ProfilePieces): string[] => {
  const { centers, expertises } = runOverwrite(p);
  const { expertisesWithGroups, normalizedGroupExpertises } = resolveExpertisesWithGroups(p, expertises);
  return getProdLikeRelatedSpecialityLinks({
    centers,
    group_expertises: expertisesWithGroups.group_expertises?.length
      ? expertisesWithGroups.group_expertises
      : normalizedGroupExpertises,
  });
};
